// src/pages/Home.tsx
import { AdBanner } from '@/components/AdBanner';
import { Calculator } from '@/components/Calculator';
import { SEO } from '@/components/SEO';
import { EmbedsShowcase } from '@/components/EmbedsShowcase';
import { 
  Info, 
  Wallet2, 
  LineChart, 
  Percent, 
  Clock, 
  Target, 
  DollarSign, 
  Calendar, 
  TrendingUp, 
  BarChart2, 
  PieChart
} from 'lucide-react';
import { Link } from 'react-router-dom';

export function Home() {
  // Create schema markup for the home page
  const homePageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Home Buying Calculators & Mortgage Calculator",
    "description": "Interactive home buying tools designed to help you make smarter decisions. Includes home affordability calculator, mortgage calculator, future purchase date calculator, and down payment savings calculator.",
    "mainEntity": {
      "@type": "SoftwareApplication",
      "name": "BuyHomeCalc Interactive Calculators",
      "applicationCategory": "FinanceApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "156",
        "bestRating": "5",
        "worstRating": "1"
      }
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://buyhomecalc.com"
        }
      ]
    }
  };

  // Create schema markup for each calculator type
  const calculatorSchemas = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Home Affordability Calculator",
      "applicationCategory": "FinanceApplication",
      "applicationSubCategory": "Mortgage Calculator",
      "operatingSystem": "Web",
      "description": "Calculate how much house you can afford based on your income, down payment, and financial situation.",
      "url": "https://buyhomecalc.com/home-affordability-calculator",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Mortgage Payment Calculator",
      "applicationCategory": "FinanceApplication",
      "applicationSubCategory": "Mortgage Calculator",
      "operatingSystem": "Web",
      "description": "Calculate your monthly mortgage payment based on home price, down payment, and interest rate.",
      "url": "https://buyhomecalc.com/mortgage-payment-calculator",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Purchase Date Calculator",
      "applicationCategory": "FinanceApplication",
      "applicationSubCategory": "Mortgage Calculator",
      "operatingSystem": "Web",
      "description": "Find out when you'll be ready to buy based on your savings rate and target price.",
      "url": "https://buyhomecalc.com/purchase-timeline-calculator",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Down Payment Calculator",
      "applicationCategory": "FinanceApplication",
      "applicationSubCategory": "Mortgage Calculator",
      "operatingSystem": "Web",
      "description": "Calculate how much you need to save monthly to reach your down payment goal.",
      "url": "https://buyhomecalc.com/down-payment-calculator",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }
  ];

  // Combine all schemas into one array
  const allSchemas = [homePageSchema, ...calculatorSchemas];

  return (
    <div>
      <SEO 
        schema={allSchemas}
      />

      <div className="bg-background">
        <div className="max-w-[1280px] mx-auto px-2 sm:px-4 py-4">
          {/* Calculator Selection Cards - IMPROVED WITH BORDERS */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Home Buying Calculators</h1>
            <p className="text-gray-600 mb-6">
              Interactive home buying tools designed to help you make smarter decisions. Home affordability calculator, mortgage calculator, future purchase date calculator, and down payment savings calculator.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Card 1: Home Affordability - Now with border styling */}
              <Link 
                to="/home-affordability-calculator" 
                className="bg-white rounded-lg border border-gray-200 hover:border-primary transition-colors shadow-sm hover:shadow-md p-4 flex flex-col"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-primary flex-shrink-0" />
                  <h2 className="text-lg font-semibold text-gray-900">Home Affordability</h2>
                </div>
                <p className="text-sm text-gray-600 mb-3">Calculate how much house you can afford based on your income and finances.</p>
                <div className="mt-auto pt-2 text-sm font-medium text-primary">
                  Find your price range →
                </div>
              </Link>

              {/* Card 2: Mortgage Payment - Now with border styling */}
              <Link 
                to="/mortgage-payment-calculator" 
                className="bg-white rounded-lg border border-gray-200 hover:border-primary transition-colors shadow-sm hover:shadow-md p-4 flex flex-col"
              >
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-primary flex-shrink-0" />
                  <h2 className="text-lg font-semibold text-gray-900">Mortgage Payment</h2>
                </div>
                <p className="text-sm text-gray-600 mb-3">Calculate your monthly payment based on home price, down payment, and interest rate.</p>
                <div className="mt-auto pt-2 text-sm font-medium text-primary">
                  Estimate payments →
                </div>
              </Link>

              {/* Card 3: Purchase Date - Now with border styling */}
              <Link 
                to="/purchase-timeline-calculator" 
                className="bg-white rounded-lg border border-gray-200 hover:border-primary transition-colors shadow-sm hover:shadow-md p-4 flex flex-col"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-primary flex-shrink-0" />
                  <h2 className="text-lg font-semibold text-gray-900">Future Purchase</h2>
                </div>
                <p className="text-sm text-gray-600 mb-3">Find out when you'll be ready to buy based on your savings rate and target price.</p>
                <div className="mt-auto pt-2 text-sm font-medium text-primary">
                  Plan your timeline →
                </div>
              </Link>

              {/* Card 4: Down Payment - Now with border styling */}
              <Link 
                to="/down-payment-calculator" 
                className="bg-white rounded-lg border border-gray-200 hover:border-primary transition-colors shadow-sm hover:shadow-md p-4 flex flex-col"
              >
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-primary flex-shrink-0" />
                  <h2 className="text-lg font-semibold text-gray-900">Down Payment</h2>
                </div>
                <p className="text-sm text-gray-600 mb-3">Calculate how much you need to save monthly to reach your down payment goal.</p>
                <div className="mt-auto pt-2 text-sm font-medium text-primary">
                  Set savings goals →
                </div>
              </Link>
            </div>
          </div>
          
          {/* Default Calculator */}
          <Calculator defaultTarget="price" />

          {/* Add the AdBanner component here, just before the Understanding Home Affordability section */}
          <AdBanner />

          <div className="mt-4 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-1 p-3">
              <Info className="w-8 h-8 text-primary" />
              <h2 className="text-xl font-semibold text-gray-900">Understanding Home Affordability</h2>
            </div>
          
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 px-4 pb-4">
              <div className="space-y-0.5 p-2">
                <div className="flex items-center gap-2">
                  <Wallet2 className="w-4 h-4 text-gray-900" />
                  <h3 className="font-semibold text-gray-900">Income</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Traditional lending guidelines suggest keeping housing costs below 28% of gross income, with total debts below a max of 45%. The calculator defaults to 32%, which can be adjusted.
                </p>
              </div>
          
              <div className="space-y-0.5 p-2">
                <div className="flex items-center gap-2">
                  <LineChart className="w-4 h-4 text-gray-900" />
                  <h3 className="font-semibold text-gray-900">Down Payment</h3>
                </div>
                <p className="text-sm text-gray-600">
                  20% down avoids mortgage insurance and improves terms, though many buyers start with 5%. The calculator assumes a 0% growth rate on down payment savings over time.
                </p>
              </div>
          
              <div className="space-y-0.5 p-2">
                <div className="flex items-center gap-2">
                  <Percent className="w-4 h-4 text-gray-900" />
                  <h3 className="font-semibold text-gray-900">Interest Rate</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Small rate changes significantly impact your purchasing power. A 1% rate increase can reduce your maximum home price by 10% or more.
                </p>
              </div>
          
              <div className="space-y-0.5 p-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-900" />
                  <h3 className="font-semibold text-gray-900">Amortization</h3>
                </div>
                <p className="text-sm text-gray-600">
                  The calculator's default loan term is 30 years. You can adjust the mortgage term by clicking the "Edit Inputs" button next to the objective selector.
                </p>
              </div>
            </div>
          </div>

          {/* Articles Section */}
          <div className="mt-6">
            <div className="space-y-6">

              {/* Maximum Home Price Section */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <Target className="w-6 h-6 text-primary" />
                  <h3 className="text-lg font-semibold text-gray-900">Maximum Home Price</h3>
                </div>
                <div className="prose prose-sm max-w-none text-gray-600">
                  <p>
                    Affordability calculators typically determine optimal home price ranges using conservative debt-to-income ratios around 35%, which helps promote sustainable homeownership. According to <a href="https://www.federalreserve.gov/publications/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Federal Reserve research</a>, households spending more than 35% on housing face significantly higher financial stress.
                  </p>
                  
                  <p>
                    This approach factors in income, savings trajectory, and market conditions to provide a realistic price range that maintains financial flexibility for other life goals. Recent data shows that homeowners who stay within this threshold have a 50% lower risk of mortgage default and maintain better overall financial health.
                  </p>
                  
                  <p>
                    Interactive tools like BuyHomeCalc.com can help explore different scenarios and identify optimal price ranges based on individual financial situations. The <a href="https://www.consumerfinance.gov/owning-a-home/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Consumer Financial Protection Bureau</a> offers resources to help homebuyers understand how different factors affect their maximum affordable purchase price and avoid overextending financially.
                  </p>
                </div>
              </div>

              {/* Debt-to-Income Ratio Section */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <BarChart2 className="w-6 h-6 text-primary" />
                  <h3 className="text-lg font-semibold text-gray-900">Debt-to-Income Ratio</h3>
                </div>
                <div className="prose prose-sm max-w-none text-gray-600">
                  <p>
                    Debt-to-income ratio compares monthly debt obligations to gross monthly income. While lenders may accept up to 43%, many financial experts suggest keeping housing costs under 35% to maintain financial flexibility. According to <a href="https://www.consumerfinance.gov/ask-cfpb/what-is-a-debt-to-income-ratio-why-is-the-43-debt-to-income-ratio-important-en-1791/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Consumer Financial Protection Bureau</a>, debt-to-income ratio is one of the most important factors lenders consider when evaluating mortgage applications.
                  </p>
                  
                  <p>
                    This conservative approach provides a crucial buffer for unexpected expenses, market changes, and other financial goals. <a href="https://www.federalreserve.gov/publications/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Federal Reserve research</a> shows that borrowers with DTI ratios below 35% are significantly more likely to successfully navigate economic downturns and maintain their homeownership long-term.
                  </p>
                  
                  <p>
                    <a href="https://www.fanniemae.com/research-and-insights/surveys-indices/national-housing-survey" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Fannie Mae's National Housing Survey</a> indicates that lower DTI ratios correlate with higher homeowner satisfaction and financial stability. Calculators can help identify the balance between maximizing buying power and maintaining financial stability. Adjusting different variables shows how they affect debt-to-income ratio and overall affordability.
                  </p>
                </div>
              </div>

              {/* Visualizing Homebuying Scenarios Section */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <PieChart className="w-6 h-6 text-primary" />
                  <h3 className="text-lg font-semibold text-gray-900">Visualizing Homebuying Scenarios</h3>
                </div>
                <div className="prose prose-sm max-w-none text-gray-600">
                  <p>
                    Visual comparison of homebuying scenarios significantly enhances decision-making quality and confidence. According to research from the <a href="https://www.tandfonline.com/journals/hbhf20" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Journal of Behavioral Finance</a>, visual representation of financial scenarios improves comprehension by approximately 30% compared to text-only information, with interactive visualizations showing particular effectiveness for complex decisions with multiple variables.
                  </p>
                  
                  <p>
                    The <a href="https://www.mba.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Mortgage Bankers Association</a> explains that comprehensive scenario analysis should include stress-testing different economic conditions, examining various down payment and loan term options, and evaluating the impact of potential life changes on affordability. Their guidelines recommend comparing at least three distinct scenarios when making major housing decisions, with each scenario incorporating different assumptions about income growth, interest rate movements, and housing market trends.
                  </p>
                  
                  <p>
                    <a href="https://www.hbs.edu/behavioral-finance-and-financial-stability/research/Pages/default.aspx" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Harvard Business School research</a> demonstrates that decision-makers who use visual scenario modeling typically consider 40% more variables in their planning process and express 25% higher confidence in their final decisions. Their analysis indicates effective visualization tools help homebuyers identify optimal paths to ownership by making complex trade-offs more intuitive and comprehensible.
                  </p>
                </div>
              </div>

              {/* The 28/36 Rule for Affordability Section */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <DollarSign className="w-6 h-6 text-primary" />
                  <h3 className="text-lg font-semibold text-gray-900">The 28/36 Rule for Affordability</h3>
                </div>
                <div className="prose prose-sm max-w-none text-gray-600">
                  <p>
                    The 28/36 rule represents a traditional mortgage affordability guideline used by many financial institutions. According to the <a href="https://www.fdic.gov/resources/consumers/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Federal Deposit Insurance Corporation</a>, this rule suggests housing costs should not exceed 28% of gross monthly income, while total debt payments (including housing) should remain below 36%.
                  </p>
                  
                  <p>
                    <a href="https://myhome.freddiemac.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Freddie Mac's borrower education resources</a> explain that housing costs in this calculation include principal, interest, taxes, insurance, and homeowners association fees (PITI+HOA). Their research indicates borrowers staying within these parameters typically experience significantly lower financial stress and mortgage default rates, particularly during economic downturns.
                  </p>
                  
                  <p>
                    While modern automated underwriting systems often approve higher ratios (up to 45-50% back-end DTI for well-qualified borrowers), financial advisors including those at the <a href="https://www.nfcc.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">National Foundation for Credit Counseling</a> continue to recommend the more conservative 28/36 approach for financial resilience. This guideline helps ensure homeowners maintain sufficient flexibility for other financial goals, unexpected expenses, and potential income fluctuations.
                  </p>
                </div>
              </div>

              {/* Budget Planning for Homeownership Section */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp className="w-6 h-6 text-primary" />
                  <h3 className="text-lg font-semibold text-gray-900">Budget Planning for Homeownership</h3>
                </div>
                <div className="prose prose-sm max-w-none text-gray-600">
                  <p>
                    Comprehensive budget planning extends beyond mortgage qualification to ensure sustainable long-term homeownership. According to the <a href="https://www.jchs.harvard.edu/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Harvard Joint Center for Housing Studies</a>, homeowners who develop detailed budgets covering all housing expenses experience approximately 60% lower likelihood of financial distress compared to those focusing primarily on mortgage payments. Their research emphasizes including variable costs like utilities, maintenance, and future renovations in homeownership budgeting.
                  </p>
                  
                  <p>
                    The <a href="https://www.nfcc.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">National Foundation for Credit Counseling</a> recommends the 28/36/25 budgeting approach for homeowners: limiting housing costs to 28% of gross income, total debt payments to 36%, and maintaining 25% of the budget for savings and discretionary spending. Their counselor data indicates homeowners who maintain these ratios report significantly higher financial satisfaction and resilience against unexpected expenses or income disruptions.
                  </p>
                  
                  <p>
                    <a href="https://www.consumerreports.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Consumer Reports'</a> homeownership research highlights the importance of seasonally adjusted budgeting that accounts for periodic expenses like property taxes, insurance premiums, and seasonal maintenance. Their analysis shows that new homeowners often underestimate these variable costs by 15-25%, with the first two years of ownership typically revealing the true cost pattern of individual properties.
                  </p>
                </div>
              </div>

              {/* Total Monthly Costs Section */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <Calendar className="w-6 h-6 text-primary" />
                  <h3 className="text-lg font-semibold text-gray-900">Total Monthly Costs</h3>
                </div>
                <div className="prose prose-sm max-w-none text-gray-600">
                  <p>
                    Comprehensive housing costs extend beyond mortgage payments to include multiple expenses. According to the <a href="https://www.jchs.harvard.edu/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Harvard Joint Center for Housing Studies</a>, total monthly homeownership costs typically include mortgage principal and interest, property taxes, homeowners insurance, mortgage insurance (if applicable), utilities, maintenance, and HOA/condo fees (in applicable communities).
                  </p>
                  
                  <p>
                    The <a href="https://www.consumerfinance.gov/owning-a-home/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Consumer Financial Protection Bureau</a> recommends budgeting 1-4% of home value annually for maintenance and repairs, with older homes typically requiring higher percentages and newer construction falling at the lower end of the range. Their research indicates many first-time buyers underestimate non-mortgage expenses, leading to financial stress despite qualifying for the mortgage itself.
                  </p>
                  
                  <p>
                    The <a href="https://content.naic.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">National Association of Insurance Commissioners</a> reports that geographic location significantly impacts insurance costs, with identical properties potentially having 200-300% insurance cost variations based on natural disaster risk, crime rates, and distance from fire protection services. Utility cost estimators from the <a href="https://www.energy.gov/energysaver/save-electricity-and-fuel/appliances-and-electronics/estimating-appliance-and-home" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Department of Energy</a> can help project expenses based on local rates, home size, and efficiency ratings.
                  </p>
                </div>
              </div>

            </div>
          </div>

          {/* Using EmbedsShowcase component */}
          <EmbedsShowcase />

        </div>
      </div>
    </div>
  );
}